<script>
import { scaleLinear } from 'd3-scale';
import { line, curveCardinal } from 'd3-shape';
import YAxis from './components/yAxis.svelte';
import Globe from './components/Globe.svelte';

let { width, windData } = $props();
const margin = { left: 0, top: 14, right: 70, bottom: 28 };

const currentYear = 2026;
// Key of the climatological average column in windData
const AVG_KEY = '1991-2020_Average';

// windData is a wide table with one row per calendar day (Jan 1 first,
// Dec 31 last). We use each row's array index as its day-of-year (0-based),
// so April 1 is index 90 and Dec 31 is index 364.
const DAY_START = 90; // 1-Apr
const DAY_END = 364; // 31-Dec

// Atlantic hurricane season, June 1 – Nov 30 (day-of-year indices)
const SEASON_START = 151; // 1-Jun
const SEASON_END = 333; // 30-Nov

// Day-of-year index (0-based) for the first of each month, April–December
const months = [
    { label: 'Apr', day: 90 },
    { label: 'May', day: 120 },
    { label: 'Jun', day: 151 },
    { label: 'Jul', day: 181 },
    { label: 'Aug', day: 212 },
    { label: 'Sep', day: 243 },
    { label: 'Oct', day: 273 },
    { label: 'Nov', day: 304 },
    { label: 'Dec', day: 334 },
];

let height = $state();

let chartwidth = $derived(width - margin.left - margin.right);
let chartheight = $derived(height - margin.top - margin.bottom);

let isMobile = $derived(width < 460);

// Reshape the wide windData table into one series per year (plus the average),
// keeping only the April–December window and dropping non-numeric cells
// (empty strings, "NaN" placeholders for future dates).
let grouped = $derived.by(() => {
    if (!windData || !windData.length) return null;
    const keys = Object.keys(windData[0]).filter(k => k !== 'date');
    const rank = s => (s.isCurrent ? 2 : s.isAverage ? 1 : 0);
    return keys
        .map(key => {
            const values = [];
            windData.forEach((row, i) => {
                if (i < DAY_START || i > DAY_END) return;
                const value = parseFloat(row[key]);
                if (Number.isNaN(value)) return;
                values.push({ day: i, value, date: row.date });
            });
            const isAverage = key === AVG_KEY;
            const isCurrent = key === String(currentYear);
            return { key, year: isAverage ? key : +key, values, isAverage, isCurrent };
        })
        .filter(s => s.values.length)
        .sort((a, b) => rank(a) - rank(b));
});

// Split lines so the axis can sit in front of the gray background years but
// behind the highlighted lines (the average and the current year).
let backgroundSeries = $derived(grouped ? grouped.filter(s => !s.isAverage && !s.isCurrent) : []);
let foregroundSeries = $derived(grouped ? grouped.filter(s => s.isAverage || s.isCurrent) : []);

let xScale = $derived(
    chartwidth ? scaleLinear().domain([DAY_START, DAY_END]).range([0, chartwidth]) : null
);
let yScale = $derived(
    chartheight ? scaleLinear().domain([80, 0]).range([0, chartheight]) : null
);

let lineGen = $derived(
    xScale && yScale
        ? line()
              .x(d => xScale(+d.day))
              .y(d => yScale(+d.value))
              .curve(curveCardinal)
        : null
);

// End point of the current-year line, for the pulsing marker + label
let currentSeries = $derived(grouped ? grouped.find(d => d.isCurrent) : null);
let lastPoint = $derived(
    currentSeries && currentSeries.values.length
        ? currentSeries.values[currentSeries.values.length - 1]
        : null
);

const fmt = v => v.toFixed(1);

const MONTHS_FULL = {
    Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April',
    May: 'May', Jun: 'June', Jul: 'July', Aug: 'August',
    Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};

// windData dates look like "9-Jul"
const fmtDate = d => {
    if (!d) return '';
    const [day, mon] = d.split('-');
    return `${MONTHS_FULL[mon] || mon} ${day}, ${currentYear}`;
};

const fmtShortDate = d => {
    if (!d) return '';
    const [day, mon] = d.split('-');
    return `${mon} ${day}`;
};

// Hover state
let svgEl = $state();
let hovered = $state(null); // hovered series
let hoverPoint = $state(null); // nearest data point under the cursor
let pointer = $state({ x: 0, y: 0 }); // cursor position for the tooltip

// Flip the tooltip to the left of the cursor near the right edge so it
// doesn't get clipped.
let tooltipFlip = $derived(width ? pointer.x > width * 0.6 : false);

// End-of-line label for the 1991–2020 average line
let avgSeries = $derived(grouped ? grouped.find(d => d.isAverage) : null);
let avgLabel = $derived(
    avgSeries && avgSeries.values.length && xScale && yScale
        ? (() => {
              const last = avgSeries.values[avgSeries.values.length - 1];
              return { x: xScale(+last.day), y: yScale(+last.value) };
          })()
        : null
);

function handleHover(e, s) {
    if (!svgEl || !xScale) return;
    const rect = svgEl.getBoundingClientRect();
    const day = xScale.invert(e.clientX - rect.left - margin.left);
    let nearest = s.values[0];
    let best = Infinity;
    for (const p of s.values) {
        const dist = Math.abs(+p.day - day);
        if (dist < best) {
            best = dist;
            nearest = p;
        }
    }
    hovered = s;
    hoverPoint = nearest;
    pointer = { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

function clearHover() {
    hovered = null;
    hoverPoint = null;
}
</script>


<div class="graphic-wrapper" bind:clientWidth={width} bind:clientHeight={height}>
    <div class="locator" class:mobile={isMobile}>
        <Globe size={75} />
    </div>
    {#if width && height}
        <svg bind:this={svgEl} {width} {height}>
            <g class="chart" transform="translate({margin.left}, {margin.top})">
                {#if xScale && chartheight}
                    <g class="season-band">
                        <rect
                            x={xScale(SEASON_START)}
                            y="0"
                            width={xScale(SEASON_END) - xScale(SEASON_START)}
                            height={chartheight}
                        ></rect>
                        <text
                            class="season-label"
                            x={(xScale(SEASON_START) + xScale(SEASON_END)) / 2}
                            y="12"
                        >Hurricane season</text>
                    </g>
                {/if}

                {#if xScale}
                    <g class="x-axis">
                        {#each months as m, i}
                            {#if !(isMobile && m.day === DAY_START)}
                                <text
                                    class="month-label"
                                    class:start={m.day === DAY_START}
                                    x={xScale(m.day)}
                                    y={chartheight + 16}
                                >{m.label}</text>
                            {/if}
                        {/each}
                    </g>
                {/if}

                {#if grouped && lineGen}
                    <g class="lines" class:dim={hovered}>
                        {#each backgroundSeries as s (s.year)}
                            <path
                                d={lineGen(s.values)}
                                class="year-line"
                                fill="none"
                            ></path>
                        {/each}
                    </g>

                    {#if yScale}
                        <YAxis {yScale} {chartwidth} part="lines" />
                    {/if}

                    <g class="lines" class:dim={hovered}>
                        {#each foregroundSeries as s (s.year)}
                            <path
                                d={lineGen(s.values)}
                                class="year-line"
                                class:average={s.isAverage}
                                class:current={s.isCurrent}
                                fill="none"
                            ></path>
                        {/each}
                    </g>

                    {#if yScale}
                        <YAxis {yScale} {chartwidth} unit=" (kt)" part="labels" />
                    {/if}

                    {#if hovered}
                        <path
                            class="hover-line"
                            class:average={hovered.isAverage}
                            class:current={hovered.isCurrent}
                            d={lineGen(hovered.values)}
                            fill="none"
                        ></path>
                    {/if}

                    {#if avgLabel}
                        <text class="avg-label halo" x={avgLabel.x + 6} y={avgLabel.y}>
                            <tspan x={avgLabel.x + 6}>1991–2020</tspan>
                            <tspan x={avgLabel.x + 6} dy="1.15em">average</tspan>
                        </text>
                        <text class="avg-label" x={avgLabel.x + 6} y={avgLabel.y}>
                            <tspan x={avgLabel.x + 6}>1991–2020</tspan>
                            <tspan x={avgLabel.x + 6} dy="1.15em">average</tspan>
                        </text>
                    {/if}

                    {#each grouped as s (s.year)}
                        <path
                            class="hit-area"
                            d={lineGen(s.values)}
                            fill="none"
                            role="presentation"
                            onpointerenter={(e) => handleHover(e, s)}
                            onpointermove={(e) => handleHover(e, s)}
                            onpointerleave={clearHover}
                        ></path>
                    {/each}
                {/if}

                {#if hoverPoint && xScale && yScale}
                    <circle
                        class="hover-dot"
                        class:average={hovered && hovered.isAverage}
                        class:current={hovered && hovered.isCurrent}
                        cx={xScale(+hoverPoint.day)}
                        cy={yScale(+hoverPoint.value)}
                        r="4"
                    ></circle>
                {/if}

                {#if lastPoint && xScale && yScale}
                    {@const cx = xScale(+lastPoint.day)}
                    {@const cy = yScale(+lastPoint.value)}
                    <g class="current-marker" transform="translate({cx}, {cy})">
                        <circle class="dot" r="4"></circle>
                        <text class="marker-year" x="11" y="-2">{fmtDate(lastPoint.date)}</text>
                        <text class="marker-value" x="11" y="12">{fmt(+lastPoint.value)} kt</text>
                    </g>
                {/if}
            </g>
        </svg>

        {#if hovered && hoverPoint}
            <div class="tooltip" class:flip={tooltipFlip} style="left:{pointer.x}px; top:{pointer.y}px;">
                <span class="tt-year">{hovered.isAverage ? '1991–2020 average' : hovered.year}</span>
                <span class="tt-date">{fmtShortDate(hoverPoint.date)}</span>
                <span class="tt-val">{fmt(+hoverPoint.value)} kt</span>
            </div>
        {/if}
    {/if}
</div>

<style lang='scss'>
    @import '@/styles/variables';

    .graphic-wrapper {
        width: 100%;
        position: relative;
        height: 500px;
        @media screen and (max-width:450px) {
            height: 400px;
        }
    }

    .locator {
        position: absolute;
        top: 6px;
        left: 24px;
        z-index: 3;
        pointer-events: none;

        &.mobile {
            top: auto;
            bottom: 30px;
            left:20px;
        }
    }

    .season-band {
        pointer-events: none;

        rect {
            fill: $gray2;
        }
    }

    

    .season-label {
        fill: $gray7;
        font-size: 11px;
        font-weight: 600;
        text-anchor: middle;
    }

    .year-line {
        stroke: $gray4;
        stroke-width: 1;
        pointer-events: none;

        &.average {
            stroke: $gray9;
            stroke-width: 2;
            stroke-dasharray: 5 4;
        }

        &.current {
            stroke: $blue;
            stroke-width: 2.5;
        }
    }

    // Fade the whole field while hovering so the active line stands out
    .lines.dim .year-line {
        opacity: 0.28;
        transition: opacity 0.12s ease;
    }

    .hover-line {
        fill: none;
        stroke: $gray9;
        stroke-width: 2;
        pointer-events: none;

        &.average {
            stroke: $gray9;
            stroke-width: 2.5;
            stroke-dasharray: 5 4;
        }

        &.current {
            stroke: $blue;
            stroke-width: 3;
        }
    }

    .hit-area {
        fill: none;
        stroke: transparent;
        stroke-width: 12;
        pointer-events: stroke;
        cursor: crosshair;
    }

    .hover-dot {
        fill: $gray9;
        stroke: #fff;
        stroke-width: 1.5;
        pointer-events: none;

        &.average {
            fill: $gray9;
        }

        &.current {
            fill: $blue;
        }
    }

    .avg-label {
        font-size: 12px;
        font-weight: 700;
        fill: $gray9;
        text-anchor: start;
        pointer-events: none;

        &.halo {
            fill: #fff;
            stroke: #fff;
            stroke-width: 3.5;
            stroke-linejoin: round;
        }
    }

    .month-label {
        font-size: 11px;
        fill: #666;
        text-anchor: middle;
        pointer-events: none;

        &.start {
            text-anchor: start;
        }
    }

    .current-marker {
        pointer-events: none;

        .dot {
            fill: $blue;
        }

        .marker-year {
            font-size: 12px;
            font-weight: 700;
            fill: $blue;
        }

        .marker-value {
            font-size: 12px;
            fill: $gray9;
        }
    }

    .tooltip {
        position: absolute;
        pointer-events: none;
        transform: translate(12px, -50%);
        background: #fff;
        border: 1px solid $gray4;
        border-radius: 3px;
        padding: 4px 8px;
        font-size: 12px;
        line-height: 1.2;
        white-space: nowrap;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
        z-index: 2;

        &.flip {
            transform: translate(calc(-100% - 12px), -50%);
        }

        .tt-year {
            font-weight: 700;
            color: $gray11;
        }

        .tt-date {
            margin-left: 6px;
            color: $gray7;
        }

        .tt-val {
            margin-left: 6px;
            color: $gray9;
        }
    }
</style>
